import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { InputAdornment, MenuItem, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Drawer from '../../components/Drawer'
import { RamoMark } from '../customers/common/customer-common'
import {
    STEPS,
    STATUS,
    formaPagoLabel,
    historySeed,
    insuredName,
    money,
    parseAmount,
    premiumDelta,
    primaOf,
    ramoOf,
    shortDate,
    vigorNote,
} from './renewalFormat'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }

const RenewalDrawer = ({ poliza, aseguradoras, onClose, onPatch, onRemind }) => {
    const navigate = useNavigate()
    const [shown, setShown] = useState(null)
    const active = poliza || shown
    const workflow = active?.workflow || { status: 'porcotizar' }
    const [ciaNueva, setCiaNueva] = useState('')
    const [primaNueva, setPrimaNueva] = useState('')
    const [cambios, setCambios] = useState('')
    const [primaErr, setPrimaErr] = useState(false)

    useEffect(() => {
        if (!poliza) return undefined
        setShown(poliza)
        setCiaNueva(poliza.workflow?.renovCia || poliza.aseguradora || '')
        setPrimaNueva(poliza.workflow?.renovPrima ? Number(poliza.workflow.renovPrima).toFixed(2) : '')
        setCambios(poliza.workflow?.cambios || '')
        setPrimaErr(false)
        return undefined
    }, [poliza])

    if (!active) return null

    const name = insuredName(active)
    const ramo = ramoOf(active)
    const days = active.days
    const statusMeta = STATUS[workflow.status] || STATUS.porcotizar
    const step = statusMeta.step
    const note = vigorNote(days, workflow.status)
    const currentPrima = primaOf(active)
    const renovPrima = workflow.renovPrima
    const delta = premiumDelta(currentPrima, renovPrima)
    const hist = historySeed(active, workflow)
    const cias = Array.from(new Set([
        active.aseguradora,
        ...(aseguradoras || []),
        workflow.renovCia,
    ].filter(Boolean)))

    const patch = (next) => onPatch(active.polizaId, next)

    const saveQuote = () => {
        const amount = parseAmount(primaNueva)
        if (!amount) {
            setPrimaErr(true)
            return
        }
        const nextStatus = workflow.status === 'porcotizar' ? 'cotizada' : workflow.status
        patch({
            renovPrima: amount,
            renovCia: ciaNueva || active.aseguradora,
            cambios: cambios.trim(),
            status: nextStatus,
        })
    }

    const markRenovada = () => {
        patch({ status: 'renovada' })
        onClose()
        navigate('/polizas/nuevo', { state: { polizaId: active.polizaId, action: 'renovar', backUrl: '/renovaciones' } })
    }

    const markNoRenueva = () => {
        patch({ status: 'norenueva' })
        onClose()
    }

    return (
        <Drawer open={Boolean(poliza)} onClose={onClose} bare width={540}>
            <div className="flex h-full flex-col bg-k-surface">
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-k-line px-6 py-5">
                <div className="flex min-w-0 items-center gap-3">
                    <RamoMark ramo={ramo} boxed />
                    <div className="flex min-w-0 flex-col gap-0.5">
                        <h2 className="m-0 truncate text-xl font-bold text-k-ink">Renovación {active.noPoliza || ''}</h2>
                        <span className="truncate text-[13px] text-k-muted">{name} · {ramo || 'Sin ramo'}</span>
                    </div>
                </div>
                <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-k-surface hover:bg-k-hover" onClick={onClose}>
                    <CloseIcon style={{ fontSize: 18 }} />
                </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
                <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm font-semibold ${note.alertBg} ${note.alertFg}`}>
                    <AccessTimeOutlinedIcon className="shrink-0" style={{ fontSize: 20 }} />
                    <span>Vigencia actual: {shortDate(active.fechaVigenciaA)} · {note.note}</span>
                </div>

                <div className="flex flex-col gap-2.5">
                    <span className="text-[13px] font-bold uppercase tracking-[0.05em] text-k-muted">Avance de la renovación</span>
                    <ol aria-label="Pasos de la renovación" className="m-0 grid list-none grid-cols-4 gap-1.5 p-0">
                        {STEPS.map((item, index) => {
                            const done = step >= 0 && index <= step
                            const current = index === step
                            const barClass = step < 0
                                ? 'bg-k-danger-soft'
                                : done
                                    ? (step === 3 ? 'bg-k-ok' : 'bg-k-primary')
                                    : 'bg-k-line'
                            const btnClass = current
                                ? 'text-k-ink font-extrabold'
                                : done
                                    ? 'text-k-ink-2 font-semibold'
                                    : 'text-k-muted-2 font-semibold'
                            return (
                                <li key={item.id} className="flex min-w-0 flex-col gap-1.5">
                                    <span className={`h-1.5 rounded-[3px] ${barClass}`} />
                                    <button
                                        type="button"
                                        aria-current={current ? 'step' : undefined}
                                        className={`min-h-8 border-0 bg-transparent p-0 text-left text-xs ${btnClass}`}
                                        onClick={() => patch({ status: item.id })}
                                    >
                                        {item.label}
                                    </button>
                                </li>
                            )
                        })}
                    </ol>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line p-3.5">
                        <span className="text-xs font-semibold text-k-muted">Prima actual</span>
                        <span className="text-xl font-bold tabular-nums text-k-ink">{money(currentPrima, active.moneda)}</span>
                        <span className="text-xs text-k-muted">{active.aseguradora || '—'} · {formaPagoLabel(active.formaPago)}</span>
                    </div>
                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-primary/30 bg-k-accent-soft p-3.5">
                        <span className="text-xs font-bold text-k-accent-ink">Prima de renovación</span>
                        <span className={`text-xl font-bold tabular-nums ${renovPrima ? 'text-k-ink' : 'text-k-muted-2'}`}>
                            {renovPrima ? money(renovPrima, active.moneda) : 'Sin cotizar'}
                        </span>
                        <span className={`text-xs font-bold ${delta.className}`}>{delta.label || '—'}</span>
                    </div>
                </div>

                <div className="flex flex-col gap-3 rounded-[14px] border border-k-line bg-k-sunken p-4">
                    <span className="text-sm font-bold text-k-ink">Registrar cotización de renovación</span>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <TextField
                            className="v2-field v2-select"
                            variant="filled"
                            fullWidth
                            select
                            label="Aseguradora"
                            value={ciaNueva}
                            onChange={(event) => setCiaNueva(event.target.value)}
                            slotProps={{ select: selectMenu }}
                        >
                            {cias.map((cia) => <MenuItem key={cia} value={cia}>{cia}</MenuItem>)}
                        </TextField>
                        <TextField
                            className="v2-field"
                            variant="filled"
                            fullWidth
                            label="Prima anual nueva"
                            value={primaNueva}
                            error={primaErr}
                            inputMode="decimal"
                            onChange={(event) => {
                                setPrimaNueva(event.target.value)
                                setPrimaErr(false)
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                },
                            }}
                        />
                    </div>
                    {primaErr && <span role="alert" className="-mt-1 text-[13px] font-medium text-k-danger">Escribe la prima de la cotización.</span>}
                    <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-k-ink">
                        <span>Cambios respecto a la póliza actual <span className="font-normal text-k-muted">Opcional</span></span>
                        <textarea
                            rows={2}
                            value={cambios}
                            placeholder="Ej. Se agrega cobertura de cristales; deducible baja a 5%"
                            className="resize-y rounded-xl border border-k-line-strong bg-k-surface px-3 py-2.5 text-sm font-normal text-k-ink outline-none focus:border-k-primary"
                            onChange={(event) => setCambios(event.target.value)}
                        />
                    </label>
                    <button
                        type="button"
                        className="h-10 self-start rounded-xl border border-k-ink bg-k-surface px-3.5 text-[13px] font-bold text-k-ink hover:bg-k-hover"
                        onClick={saveQuote}
                    >
                        Guardar cotización
                    </button>
                </div>

                <div className="flex flex-col gap-2">
                    <span className="text-sm font-bold text-k-ink">Historial</span>
                    {hist.map((item) => (
                        <div key={`${item.text}-${item.when}`} className="flex justify-between gap-2.5 border-t border-k-line-soft py-2 text-[13px]">
                            <span className="text-k-ink">{item.text}</span>
                            <span className="whitespace-nowrap text-k-muted">{item.when}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2.5 border-t border-k-line px-6 py-4">
                <button type="button" className="h-11 rounded-xl border-0 bg-transparent px-3.5 text-sm font-semibold text-k-danger-ink hover:bg-k-danger-soft" onClick={markNoRenueva}>
                    No renueva
                </button>
                <div className="flex flex-wrap gap-2.5">
                    <button
                        type="button"
                        className="h-11 rounded-xl border border-k-line-strong bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={() => onRemind(active)}
                    >
                        Enviar al cliente
                    </button>
                    <button
                        type="button"
                        className="h-11 rounded-xl bg-k-primary px-[18px] text-sm font-bold text-white hover:brightness-105"
                        onClick={markRenovada}
                    >
                        Marcar como renovada
                    </button>
                </div>
            </div>
            </div>
        </Drawer>
    )
}

export default RenewalDrawer
